import React, { useEffect } from 'react';
import styled from 'styled-components';
import { NavList, NavType, SubNavType } from '@/assets/data/navList';
import TextComponent from '@/components/TextComponent';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { notificationState } from '@/recoil/notification';
import useOrderQuery from '@/hooks/order/useOrderQuery';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ERequestedStatusType, Order } from '@/apis/order/interface';
import useChannelTalk from '@/utils/channelTalkService';
import { pharmacyState } from '@/recoil/pharmacy';
import { ReactComponent as New } from '@/assets/images/layout/newIcon.svg';
import { MAIN_NOTICE, neverSeeAgainState } from '@/recoil/neverSeeAgain';
import moment from 'moment';
import theme from '@/styles/theme';

const NavigationMenu = () => {
  const location = useLocation();
  const history = useHistory();
  const [notification, setNotification] = useRecoilState(notificationState);
  const { requestedOrdersQuery, preparingOrdersQuery } = useOrderQuery();
  const { latestNotice } = useRecoilValue(neverSeeAgainState);

  const pharmacy = useRecoilValue(pharmacyState);

  const { data: requestedOrders } = requestedOrdersQuery({
    page: 1,
    limit: 21,
    startDate: moment().subtract(7, 'd').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    status: ERequestedStatusType.READY,
    sort: null
  });

  const { data: preparingOrders, isLoading } = preparingOrdersQuery({
    page: 1,
    limit: 21,
    startDate: moment().subtract(7, 'd').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    status: null,
    sort: null
  });

  useEffect(() => {
    if (!isLoading) {
      const requested = requestedOrders?.list.filter(
        (item: Order) => !item.receivedAt
      );
      const preparing = preparingOrders?.list.filter(
        (item: Order) => !item.checkedAt
      );
      setNotification({
        ...notification,
        requested: requested ? requested.length : 0,
        preparing: preparing ? preparing.length : 0
      });
    }
  }, [requestedOrders, preparingOrders]);

  const isMainActivity = (pathName: string) =>
    location.pathname.includes(pathName.split('/')[1]);

  // 현재 하위 메뉴없음
  const isSubActivity = (pathName: string) =>
    location.pathname.includes(pathName.split('/')[2]);

  const onMenu = (path: string) => {
    if (path === '/service') {
      return useChannelTalk(pharmacy).show();
    }
    history.push(path);
  };
  return (
    <Wrapper>
      {NavList.map((nav: NavType, key: number) => {
        return (
          <Menu key={key}>
            {/* <MainNav to={nav.path}> */}
            <MainNav onClick={() => onMenu(nav.path)}>
              <nav.Icon
                style={{ width: 25, height: 25, marginRight: 10 }}
                opacity={isMainActivity(nav.path) ? 1 : 0.4}
              />
              <MenuText>
                {isMainActivity(nav.path) ? (
                  <em>{nav.name}</em>
                ) : (
                  <>{nav.name}</>
                )}
                {!!nav.noti && !!notification[nav.noti] && (
                  <NewBadge>
                    {notification[nav.noti] > 20
                      ? '20+'
                      : notification[nav.noti]}
                  </NewBadge>
                )}
                {nav.name === '공지사항' && latestNotice !== MAIN_NOTICE && (
                  <Badge>
                    <New />
                  </Badge>
                )}
              </MenuText>
            </MainNav>
            {/* 현재 하위 메뉴없음 */}
            {nav.sub && (
              <SubNav open={isMainActivity(nav.path)}>
                {nav.sub.map((subNav: SubNavType, key) => (
                  <SubNavItem to={subNav.path} key={key}>
                    <TextComponent
                      fontWeight={600}
                      color={
                        isSubActivity(subNav.path) ? theme.DF_COLOR.PRIMARY500 : theme.DF_COLOR.BLACK
                      }>
                      {subNav.name}
                    </TextComponent>
                  </SubNavItem>
                ))}
              </SubNav>
            )}
          </Menu>
        );
      })}
    </Wrapper>
  );
};

export default NavigationMenu;

const Wrapper = styled.div``;

const Menu = styled.div`
  width: 100%;
  padding: 20px 0 0 40px;
`;

const MainNav = styled.span`
  display: flex;
  align-items: center;

  &:hover {
    cursor: pointer;
  }
`;

const SubNav = styled.div<{ open: boolean }>`
  padding: 9px 0 0 35px;
  display: ${props => (props.open ? 'block' : 'none')};
`;

const SubNavItem = styled(Link)`
  display: block;
  padding: 9px 0;
`;

const MenuText = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  align-items: center;
  margin: 0;

  ${({ theme }) => theme.DF_TYPOGRAPHY.UI20R};
  color: ${({ theme }) => theme.DF_COLOR.GRAY400};

  em {
    margin: 0;
    font-style: normal;
    ${({ theme }) => theme.DF_TYPOGRAPHY.UI20SB};
    color: ${({ theme }) => theme.DF_COLOR.GRAY900};
    font-weight: 600;
  }

  > * {
    &:nth-child(1) {
      margin-top: 2px;
    }
  }
`;

const Badge = styled.div`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 7px;
  min-width: 20px;
  height: 20px;
`;

const NewBadge = styled(Badge)`
  border-radius: 12px;
  background-color: ${({ theme }) => theme.DF_COLOR.RED500};
  color: ${({ theme }) => theme.DF_COLOR.WHITE};
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI14SB};
  display: flex;
  margin-left: 10px;
  margin-top: 2px;
  padding: 4px;
`;
